import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle, 
  Award, 
  Users, 
  Target,
  Lightbulb,
  Heart,
  Globe,
  Building,
  Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHeader from '../components/layout/PageHeader';

const IMAGES = {
  team: "https://images.unsplash.com/photo-1565351167686-7a19c5114965?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMHdoaXRlYm9hcmQlMjBzdHJhdGVneXxlbnwwfHx8fDE3NzM3NzU5MzR8MA&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwY29sbGFib3JhdGlvbiUyMG1vZGVybiUyMG9mZmljZSUyMGdsYXNzJTIwd2FsbHN8ZW58MHx8fHwxNzczNzc1OTMxfDA&ixlib=rb-4.1.0&q=85"
};

const values = [
  {
    icon: Target,
    title: "Excellence",
    description: "We strive for excellence in every engagement, delivering solutions that exceed expectations."
  },
  {
    icon: Heart,
    title: "Integrity",
    description: "We operate with transparency and honesty, building trust with our clients and partners."
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "We embrace cutting-edge technologies and creative solutions to drive client success."
  },
  {
    icon: Users,
    title: "Partnership",
    description: "We view every client relationship as a true partnership, invested in mutual success."
  }
];

const milestones = [
  { year: "2009", title: "Founded", description: "Global IT Associates established in the US" },
  { year: "2012", title: "Enterprise Focus", description: "Expanded to serve Fortune 500 clients" },
  { year: "2017", title: "AI Integration", description: "Pioneered AI-driven talent matching" },
  { year: "2021", title: "Digital Solutions", description: "Launched full-service consulting" },
  { year: "2024", title: "Industry Leader", description: "5000+ consultants placed" }
];

const leadership = [
  { name: "Executive Team", role: "Leadership", description: "15+ years combined experience in IT staffing and consulting" },
  { name: "Technical Advisory", role: "Strategy", description: "Former Fortune 500 CIOs and technology executives" },
  { name: "Talent Operations", role: "Delivery", description: "Industry experts in recruitment and talent management" }
];

export default function AboutPage() {
  return (
    <div className="pt-20">
      <PageHeader 
        title="About Global IT Associates"
        subtitle="Two decades of excellence in IT staffing and technology consulting. We're your trusted partner for digital transformation."
        breadcrumbs={[{ label: "About Us" }]}
      />

      {/* Mission Section */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="container-padding">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
                Our Mission
              </div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Technology & Beyond
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                At Global IT Associates, we believe that the right technology and talent can transform any business. 
                Our mission is to bridge the gap between enterprise needs and exceptional IT solutions.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We combine elite talent with AI-driven innovation to accelerate business growth. 
                Whether you need top IT professionals or cutting-edge automation solutions, 
                we're committed to your success.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-6 bg-slate-50 rounded-xl">
                  <div className="text-3xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>15+</div>
                  <div className="text-sm text-slate-600 mt-1">Years in Business</div>
                </div>
                <div className="text-center p-6 bg-slate-50 rounded-xl">
                  <div className="text-3xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>200+</div>
                  <div className="text-sm text-slate-600 mt-1">Enterprise Clients</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src={IMAGES.team}
                alt="GITA Team"
                className="rounded-2xl shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#2563EB]/10 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="values-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              Our Values
            </div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="text-center p-8 bg-white rounded-2xl border border-slate-100 hover:shadow-lg transition-shadow"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="section-padding bg-white" data-testid="journey-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              Our Journey
            </div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              15+ Years of Excellence
            </h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-slate-200 hidden lg:block" />

            <div className="space-y-12 lg:space-y-0">
              {milestones.map((milestone, index) => (
                <div 
                  key={milestone.year}
                  className={`lg:flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                  data-testid={`milestone-${index}`}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16'}`}>
                    <div className="bg-white p-6 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow inline-block">
                      <div className="text-[#2563EB] font-bold text-lg">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-[#0A2540] mt-1">{milestone.title}</h3>
                      <p className="text-slate-600 mt-2">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="hidden lg:flex w-8 h-8 bg-[#2563EB] rounded-full items-center justify-center z-10 mx-auto">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <div className="lg:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="leadership-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              Leadership
            </div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Expert Team
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {leadership.map((leader, index) => (
              <div 
                key={leader.name}
                className="bg-white p-8 rounded-2xl border border-slate-100 text-center"
                data-testid={`leader-${index}`}
              >
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540]">{leader.name}</h3>
                <div className="text-[#2563EB] font-medium mt-1">{leader.role}</div>
                <p className="text-slate-600 mt-3">{leader.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white" data-testid="why-us-section">
        <div className="container-padding">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={IMAGES.office}
                alt="Modern office"
                className="rounded-2xl shadow-xl"
                loading="lazy"
              />
            </div>

            <div>
              <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
                Why Choose Us
              </div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Your Competitive Advantage
              </h2>
              
              <div className="space-y-4">
                {[
                  "AI-powered talent matching for 50% faster placements",
                  "Deep expertise across 15+ industries",
                  "Flexible engagement models (Contract, C2C, Full-time)",
                  "US-based operations with global delivery capabilities",
                  "End-to-end technology consulting services",
                  "98% client satisfaction rate"
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#2563EB] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/contact">
                  <Button className="btn-primary" data-testid="contact-btn">
                    Get in Touch
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A2540]" data-testid="cta-section">
        <div className="container-padding text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Partner with Us?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Join 200+ enterprise clients who trust Global IT Associates for their staffing and technology needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?type=hire_talent">
              <Button className="btn-accent text-base" data-testid="cta-hire-btn">
                Hire Talent
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/careers">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A2540] rounded-full px-8 py-4 text-base font-medium"
                data-testid="cta-careers-btn"
              >
                Join Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
